import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import authApi from '../api/authApi';
import settingApi from '../api/settingApi';
import { SETTING_KEYS } from '../constants';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('authToken') || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!token);
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [siteName, setSiteName] = useState('Admin Panel');
  const [userPermissions, setUserPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchUserPermissions = useCallback(async () => {
    try {
      const permissionsResponse = await authApi.getAuthUserPermissions();
      if (permissionsResponse.success && permissionsResponse.data) {
        setUserPermissions(permissionsResponse.data);
      } else {
        console.warn('Failed to fetch user permissions:', permissionsResponse.message);
        setUserPermissions([]);
      }
    } catch (error) {
      console.error('Error fetching user permissions:', error);
      setUserPermissions([]);
    }
  }, []);

  useEffect(() => {
    const loadInitialData = async () => {
      let fetchedUser = null;
      let authenticated = false;
      let emailVerified = false;
      let fetchedSiteName = 'Admin Panel';

      if (token) {
        try {
          const userResponse = await authApi.getUser();
          if (userResponse.success) {
            fetchedUser = userResponse.data;
            authenticated = true;
            emailVerified = !!userResponse.data.email_verified_at;
            await fetchUserPermissions();
          } else {
            console.error('Failed to fetch user with existing token:', userResponse.message);
            localStorage.removeItem('authToken');
            setToken(null);
          }
        } catch (error) {
          console.error('Error loading user from token:', error);
          localStorage.removeItem('authToken');
          setToken(null);
        }
      }

      try {
        const settingsResponse = await settingApi.getSettings();
        if (settingsResponse.success && settingsResponse.data) {
          fetchedSiteName = settingsResponse.data[SETTING_KEYS.SITE_NAME] || 'Admin Panel';
        } else {
          console.warn('Failed to fetch site name from settings:', settingsResponse.message);
        }
      } catch (error) {
        console.error('Error fetching site settings:', error);
      }

      setUser(fetchedUser);
      setIsAuthenticated(authenticated);
      setIsEmailVerified(emailVerified);
      setSiteName(fetchedSiteName);
      setLoading(false);
    };

    loadInitialData();
  }, [token, fetchUserPermissions]);

  const login = async (newToken, userData) => {
    localStorage.setItem('authToken', newToken);
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
    setIsEmailVerified(!!userData.email_verified_at);
    await fetchUserPermissions();
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setIsEmailVerified(false);
    setUserPermissions([]);
    authApi.logout().catch(error => console.error('Backend logout failed:', error));
  };

  const hasPermission = useCallback((permissionName) => {
    return userPermissions.includes(permissionName);
  }, [userPermissions]);

  return (
    <AuthContext.Provider value={{ user, token, isAuthenticated, isEmailVerified, siteName, userPermissions, hasPermission, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};